
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Save, X, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { logActivity, getUserName } from "@/utils/userUtils";
import { SupplierSelector } from "./suppliers/SupplierSelector";
import { Order } from "@/hooks/useOrdersManagement/types";

interface OrderEditProps {
  order: Order;
  onBack: () => void;
  onSave: () => void;
  onDelete?: () => void;
}

export const OrderEdit = ({ order, onBack, onSave, onDelete }: OrderEditProps) => {
  const [supplierCode, setSupplierCode] = useState(order.supplierName); // This will now store the supplier code
  const [deliveryDate, setDeliveryDate] = useState(order.deliveryDate);
  const [status, setStatus] = useState(order.status);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSave = async () => {
    if (!supplierCode.trim()) {
      toast({
        title: "Error",
        description: "Please select a supplier.",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('orders')
        .update({
          supplier_name: supplierCode.trim(), // This now stores the supplier code
          delivery_date: deliveryDate,
          status: status,
          updated_at: new Date().toISOString()
        })
        .eq('id', order.id);

      if (error) throw error;

      logActivity('Updated order details', { 
        orderId: order.id, 
        supplier: supplierCode,
        status: status
      });

      queryClient.invalidateQueries({ queryKey: ['orders'] });
      
      toast({
        title: "Order Updated",
        description: `Order for supplier ${supplierCode} has been updated successfully.`,
      });

      onSave();
    } catch (error: any) {
      console.error('Error updating order:', error);
      toast({
        title: "Error",
        description: "Failed to update order. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete the order for ${order.supplierName}? This will also delete all associated products and cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    try {
      // First delete all products associated with this order
      const { error: productsError } = await supabase
        .from('products')
        .delete()
        .eq('order_id', order.id);

      if (productsError) throw productsError;

      // Then delete the order
      const { error: orderError } = await supabase
        .from('orders')
        .delete()
        .eq('id', order.id);

      if (orderError) throw orderError;

      logActivity('Deleted order', { 
        orderId: order.id, 
        supplier: order.supplierName
      });

      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      
      toast({
        title: "Order Deleted",
        description: `Order for ${order.supplierName} has been deleted successfully.`,
      });

      if (onDelete) {
        onDelete();
      } else {
        onBack();
      }
    } catch (error: any) {
      console.error('Error deleting order:', error);
      toast({
        title: "Error",
        description: "Failed to delete order. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="w-full px-2 md:px-4 lg:container lg:mx-auto py-2 md:py-6">
        {/* Improved Header Layout */}
        <div className="bg-white rounded-lg shadow-sm border p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex flex-col gap-3 md:gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                <Button variant="outline" onClick={onBack} size="sm" className="shrink-0">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">Back</span>
                </Button>
                <div className="min-w-0">
                  <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 truncate">Edit Order</h1>
                  <p className="text-xs md:text-sm text-gray-600 truncate">#{order.id}</p>
                </div>
              </div>
              <Button 
                onClick={handleDelete} 
                disabled={isDeleting || isSaving} 
                variant="destructive"
                size="sm"
                className="shrink-0"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden md:inline ml-1">
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </span>
              </Button>
            </div>
          </div>
        </div>

        <Card className="w-full max-w-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Order Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Supplier Selector */}
            <SupplierSelector
              value={supplierCode}
              onValueChange={setSupplierCode}
              placeholder="Select supplier by code..."
            />

            <div>
              <label className="block text-sm font-medium mb-2">Delivery Date *</label>
              <Input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as "active" | "completed")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <h3 className="font-medium mb-2 text-sm">Order Information</h3>
              <div className="space-y-1 text-xs text-gray-600">
                <p><span className="font-medium">Products:</span> {order.productCount}</p>
                <p><span className="font-medium">Created by:</span> {order.createdBy || 'Unknown'}</p>
                <p><span className="font-medium">Documents:</span> {order.documents.length} files</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleSave} disabled={isSaving || isDeleting} className="flex-1">
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button onClick={onBack} variant="outline" className="flex-1">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
